/**
 * Level parser + validator v2. Accepts skypulse-level@1 and @2; rejects
 * malformed files with row-level errors. New in v2 (R2 §2.2/§3/§7):
 *
 *  - ramp base-height resolution (surface continuity is validated, the
 *    deferred R1 Appendix-A rule) and "ramps never in tunnel rows"
 *  - tunnel floor holes `U`: max 1 consecutive, ≥2 solid tunnel-floor rows
 *    after the entrance before the first hole, capped-arc clearability at
 *    the level's declared minThrottle
 *  - forced-air spans (no ridable lane) checked against the max crossable
 *    gap for (gravity preset × minThrottle) — the brief's static rule
 */
import { LANES, GRAVITY_PRESETS, cappedAirtimeS, jumpParamsForBpm, TUNNEL_ROOF_HEIGHT, SHIP_TOP_CLEARANCE } from "../config/tuning";
import { GLYPHS, type Cell, type LevelFile, type ParsedLevel } from "./types";

export class LevelParseError extends Error {
  constructor(
    message: string,
    readonly errors: string[],
  ) {
    super(message + "\n" + errors.join("\n"));
    this.name = "LevelParseError";
  }
}

export interface ParseOptions {
  /** Enforce exactly 3 bits (true for shipped levels, false for chunks). */
  requireBits?: boolean;
  /** Enforce a final all-'F' row (true for levels, false for chunks). */
  requireFinish?: boolean;
  /** Rows per beat of the level's song (jump-length math). */
  rowsPerBeat?: number;
  /** Song BPM — the coyote extension of jump reach is tempo-dependent. */
  bpm?: number;
}

/**
 * Max crossable forced-air span in rows for (gravity × minThrottle):
 * jump length + the coyote launch extension past the edge (the R1-era
 * "2-row gap at throttle 1" edge jump falls out of this formula).
 */
export function maxGapRowsFor(
  gravity: keyof typeof GRAVITY_PRESETS,
  minThrottle: number,
  rowsPerBeat: number,
  bpm: number,
): number {
  const airtimeBeats = GRAVITY_PRESETS[gravity].airtimeBeats;
  const coyoteBeats = 0.09 * (bpm / 60); // 90 ms of coyote in beats
  return minThrottle * rowsPerBeat * (airtimeBeats + coyoteBeats);
}

/** Capped-arc length ratio vs the free arc — BPM-invariant per preset. */
export function cappedArcRatio(gravity: keyof typeof GRAVITY_PRESETS): number {
  const jump = jumpParamsForBpm(120, gravity); // any BPM — the ratio is invariant
  const maxRise = TUNNEL_ROOF_HEIGHT - SHIP_TOP_CLEARANCE;
  return cappedAirtimeS(jump, maxRise) / jump.airtimeS;
}

export function parseLevel(file: LevelFile, opts: ParseOptions = {}): ParsedLevel {
  const { requireBits = true, requireFinish = true, rowsPerBeat = 2, bpm = 120 } = opts;
  const errors: string[] = [];

  if (file.format !== "skypulse-level@1" && file.format !== "skypulse-level@2") {
    errors.push(`unknown format "${(file as { format?: string }).format}"`);
  }
  if (!file.rows || file.rows.length === 0) {
    errors.push("level has no rows");
    throw new LevelParseError(`Level "${file.id}" is invalid`, errors);
  }
  const gravity = file.physics?.gravity ?? "normal";
  if (!GRAVITY_PRESETS[gravity]) {
    errors.push(`unknown gravity preset "${String(gravity)}"`);
  }
  const minThrottle = file.minThrottle ?? 1.0;

  const cells: Cell[][] = [];
  const bits: ParsedLevel["bits"] = [];
  const checkpointRows: number[] = [];
  let finishRow = -1;
  let bitCount = 0;

  file.rows.forEach((rowStr, row) => {
    if (rowStr.length !== LANES) {
      errors.push(`row ${row}: expected ${LANES} chars, got ${rowStr.length} ("${rowStr}")`);
      cells.push(emptyRow());
      return;
    }
    const rowCells: Cell[] = [];
    let allFinish = true;
    let anyCheckpoint = false;
    for (let lane = 0; lane < LANES; lane++) {
      const glyph = rowStr[lane]!;
      const def = GLYPHS[glyph];
      if (!def) {
        errors.push(`row ${row}, lane ${lane}: unknown glyph "${glyph}"`);
        rowCells.push({ kind: "empty", height: 0, bitIndex: -1 });
        allFinish = false;
        continue;
      }
      const cell: Cell = { kind: def.kind, height: def.height ?? 0, bitIndex: -1 };
      if (cell.kind === "bit") {
        cell.bitIndex = bitCount;
        bits.push({ row, lane, index: bitCount });
        bitCount++;
      }
      if (cell.kind === "checkpoint") anyCheckpoint = true;
      if (cell.kind !== "finish") allFinish = false;
      rowCells.push(cell);
    }
    if (anyCheckpoint && !rowCells.every((c) => c.kind === "checkpoint")) {
      errors.push(`row ${row}: checkpoint rows must be full-row 'C'`);
    }
    if (allFinish && finishRow === -1) finishRow = row;
    if (anyCheckpoint) checkpointRows.push(row);
    cells.push(rowCells);
  });

  // --- ramp resolution + continuity (redeems D-006/D-022) -------------------
  for (let lane = 0; lane < LANES; lane++) {
    let exitHeight = 0; // surface height at the exit edge of the previous row
    let prevSolid = true;
    for (let row = 0; row < cells.length; row++) {
      const cell = cells[row]![lane]!;
      switch (cell.kind) {
        case "rampUp":
        case "rampDown": {
          const dir = cell.kind === "rampUp" ? 1 : -1;
          if (!prevSolid) {
            errors.push(`row ${row}, lane ${lane}: ramp must rise from solid ground`);
          }
          cell.rampBase = exitHeight;
          cell.rampDir = dir;
          const newExit = exitHeight + dir;
          if (newExit < 0) {
            errors.push(`row ${row}, lane ${lane}: ramp descends below the road`);
          }
          exitHeight = Math.max(0, newExit);
          prevSolid = true;
          break;
        }
        case "block": {
          // exit-side mismatches are LEGAL: a crest onto lower ground is the
          // launch lip (R2 Feature C — "natural consequence of the support
          // ending"); a taller block is a dodge-able wall like any other
          exitHeight = cell.height;
          prevSolid = true;
          break;
        }
        case "empty":
        case "hazard":
          prevSolid = false;
          exitHeight = 0;
          break;
        default: {
          exitHeight = 0;
          prevSolid = true;
          break;
        }
      }
    }
  }

  // --- tunnel rules (R2 §2.2) ------------------------------------------------
  file.rows.forEach((rowStr, row) => {
    const hasRoofCell = /[TU]/.test(rowStr);
    if (hasRoofCell && /[/\\]/.test(rowStr)) {
      errors.push(`row ${row}: ramps may not appear in tunnel rows`);
    }
  });
  for (let lane = 0; lane < LANES; lane++) {
    let tunnelFloorRun = 0; // consecutive T rows in this lane
    let holeRun = 0;
    for (let row = 0; row < cells.length; row++) {
      const kind = cells[row]![lane]!.kind;
      if (kind === "tunnelHole") {
        holeRun++;
        if (holeRun > 1) {
          errors.push(`row ${row}, lane ${lane}: tunnel holes are limited to 1 consecutive row`);
        }
        if (tunnelFloorRun < 2 && holeRun === 1) {
          errors.push(
            `row ${row}, lane ${lane}: a hole needs ≥ 2 rows of tunnel floor after the entrance`,
          );
        }
        tunnelFloorRun = 0;
      } else if (kind === "tunnel") {
        tunnelFloorRun++;
        holeRun = 0;
      } else {
        tunnelFloorRun = 0;
        holeRun = 0;
      }
    }
  }

  // --- solvability vs (gravity × minThrottle) — the brief's static rule ------
  const airtimeBeats = GRAVITY_PRESETS[gravity]?.airtimeBeats ?? 1;
  const maxGapRows = maxGapRowsFor(gravity, minThrottle, rowsPerBeat, bpm);
  let forcedAir = 0;
  for (let row = 0; row < cells.length; row++) {
    const ridable = cells[row]!.some(
      (c) => c.kind !== "empty" && c.kind !== "hazard" && c.kind !== "tunnelHole",
    );
    if (!ridable) {
      forcedAir++;
      if (forcedAir > maxGapRows) {
        errors.push(
          `row ${row}: forced-air span exceeds ${maxGapRows} rows ` +
            `(gravity ${gravity}, minThrottle ${minThrottle})`,
        );
      }
    } else {
      forcedAir = 0;
    }
  }
  // capped-arc clearability for holes (per lane; MAX_HOLE_RUN already ≤ 1)
  const cappedRows = cappedArcRatio(gravity) * airtimeBeats * rowsPerBeat * minThrottle;
  if (cappedRows < 1.2) {
    for (let lane = 0; lane < LANES; lane++) {
      for (let row = 0; row < cells.length; row++) {
        if (cells[row]![lane]!.kind === "tunnelHole") {
          errors.push(
            `row ${row}, lane ${lane}: hole not clearable under the capped arc ` +
              `(gravity ${gravity}, minThrottle ${minThrottle})`,
          );
        }
      }
    }
  }

  if (requireFinish) {
    if (finishRow === -1) {
      errors.push("no all-'F' finish row found");
    } else if (finishRow !== file.rows.length - 1) {
      errors.push(`finish row must be the final row (found at ${finishRow} of ${file.rows.length - 1})`);
    }
  }
  if (requireBits && bitCount !== 3) {
    errors.push(`level must contain exactly 3 bits ('o'), found ${bitCount}`);
  }

  if (errors.length > 0) {
    throw new LevelParseError(`Level "${file.id}" is invalid`, errors);
  }

  return {
    id: file.id,
    name: file.name,
    songId: file.songId,
    world: file.world,
    cells,
    numRows: cells.length,
    bits,
    checkpointRows,
    finishRow,
    medalTimesMs: file.medalTimesMs,
    palette: file.palette ?? `world${file.world}`,
    prompts: file.prompts ?? [],
    gravity,
    minThrottle,
  };
}

function emptyRow(): Cell[] {
  return Array.from({ length: LANES }, () => ({
    kind: "empty" as const,
    height: 0,
    bitIndex: -1,
  }));
}
